import useConfirm from "@/context/useConfirm";
import { getShortId } from "@/lib/string";
import { deployToEnvironment, FileEnvironment, mutateVersion } from "@/services/files.service";
import { Environment } from "@/types/app/environment";
import { File } from "@/types/app/file";
import Button from "@components/library/Button";
import { LabelDot } from "@components/library/Entities/EntityIcon";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import { Tag } from "@components/library/Tag";
import { hlToast, hlToastApiError, ToastVariant } from "@components/library/Toast";
import { ArrowLeftIcon, CheckIcon } from "@heroicons/react/outline";

export interface ConfirmDeploymentDialogPageProps {
  fileId: string;
  selectedVersion: File;
  onClose: () => void;
  backAction: () => void;
  environmentsSelected: Environment[];
  onDeploy: () => void;
  fileEnvironments: FileEnvironment[];
  display: "version" | "environments";
}

export const ConfirmDeploymentDialogPage = ({
  fileId,
  selectedVersion,
  onClose,
  backAction,
  display,
  environmentsSelected,
  fileEnvironments,
  onDeploy,
}: ConfirmDeploymentDialogPageProps) => {
  const confirm = useConfirm();
  const deploymentType = "Version";
  const defaultEnvironment = environmentsSelected?.find((environment) => environment.tag === "default");

  const hasSomethingDeployed = (environment: Environment) => {
    return fileEnvironments?.find((env) => env.id === environment.id && env.file !== null);
  };

  return (
    <>
      <div className="flex flex-col  justify-between align-middle">
        <div className="flex flex-col items-center text-center">
          {display === "environments" ? (
            <div className="py-40">
              <div className="text-14-20">You are about to deploy to</div>
              <div className="flex min-w-0 max-w-full flex-row justify-center pb-40 pt-8 align-middle">
                <span className="flex flex-row flex-wrap  items-center gap-4">
                  {environmentsSelected.map((env) => (
                    <Tag key={env.id} className="rounded border bg-white text-14-16 font-medium">
                      <div className="flex flex-row items-center gap-6 py-4">
                        <LabelDot
                          size={16}
                          type={env.name === "production" ? "production" : "other"}
                          empty={!hasSomethingDeployed(env)}
                        />
                        {env.name}
                      </div>
                    </Tag>
                  ))}
                </span>
              </div>
            </div>
          ) : (
            <div className="py-40">
              <div className="text-14-20">You are about to deploy:</div>
              <div className="flex min-w-0 max-w-full flex-row justify-center pb-40 pt-8 align-middle">
                <span className="flex flex-row flex-wrap  items-center gap-4">
                  {selectedVersion && (
                    <FileLabelWithPopover
                      fileId={fileId}
                      versionId={selectedVersion.version_id}
                      size={24}
                      displayFile={false}
                      versionPopoverProps={{
                        isEditable: false,
                        showOpenButton: false,
                      }}
                      tooltipContentProps={{
                        side: "top",
                      }}
                    />
                  )}
                </span>
              </div>
            </div>
          )}
          <div className="flex w-full flex-row justify-between gap-6 align-middle">
            <Button size={36} IconLeft={ArrowLeftIcon} styling="outline" onClick={backAction}>
              Back
            </Button>
            <Button
              size={36}
              styling={"solid"}
              shade={"black"}
              onClick={async () => {
                // If the deploy affects the default environment, show an extra confirmation step
                if (defaultEnvironment) {
                  await onClose();
                  await confirm({
                    title: (
                      <div className="w-full">
                        <p className="overflow-hidden text-ellipsis">
                          You are about to deploy to <span className="font-bold">{defaultEnvironment?.name}</span>
                        </p>
                      </div>
                    ),
                    content: (
                      <>
                        <span>This will affect any API calls that rely on the default environment.</span>
                        <br />
                        <br />
                        <span>Are you sure you want to continue?</span>
                      </>
                    ),
                    confirmationText: "Deploy",
                    cancellationText: "Cancel",
                  });
                }
                try {
                  // TODO(v5): Consider implementing a new endpoint for deploying a version to multiple environments at once.
                  await Promise.all(
                    environmentsSelected.map((environment) => {
                      return deployToEnvironment({
                        fileId,
                        environmentId: environment.id,
                        versionId: selectedVersion.version_id,
                      });
                    }),
                  );
                  // Just stuck a mutate here as it's likely being used (e.g. in Editors).
                  // Other uses (e.g. in list versions etc) need to handle their own state refreshes
                  // with the `onDeploy` callback.
                  mutateVersion({ fileId, versionId: selectedVersion.version_id });

                  onDeploy();
                  onClose();
                  const deployedVersion = selectedVersion.version_name
                    ? selectedVersion.version_name
                    : `#${getShortId(selectedVersion.version_id)}`;
                  hlToast({
                    title: `${deploymentType} deployed`,
                    description: (
                      <>
                        <span>{deploymentType}</span> <span className="font-bold">{deployedVersion}</span> has been
                        deployed to{" "}
                        <span className="font-bold">{environmentsSelected.map(({ name }) => name).join(", ")}</span>
                      </>
                    ),
                    variant: ToastVariant.Success,
                  });
                } catch (error) {
                  hlToastApiError({
                    error,
                    titleFallback: "Deployment failed",
                    descriptionFallback: `Failed to deploy ${deploymentType}.`,
                  });
                }
              }}
              IconLeft={CheckIcon}
            >
              Deploy
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
